import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import styles from './progress.module.scss';
const Progress = ({ currentValue = 0, indeterminateStyle = 'disappear', maxValue = 100, showProgressValue = false, size = 'md', status = 'default', type = 'determinate', width = 250, RTL = false, statusText = '', label = 'progress bar', }) => {
    const progressTrackRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const progressPercent = useMemo(() => currentValue / maxValue, [currentValue]);
    const progressPercentValue = useMemo(() => {
        const val = Math.round(progressPercent * 100);
        return val > 5 ? `${val}%` : 0;
    }, [progressPercent]);
    const canShowProgressValue = useMemo(() => showProgressValue &&
        progressPercentValue !== 0 &&
        type !== 'indeterminate' &&
        size !== 'sm', [progressPercentValue]);
    const fillWidth = useMemo(() => {
        const trackWidth = progressTrackRef.current?.clientWidth;
        if (trackWidth) {
            return Math.round(trackWidth * progressPercent);
        }
        return 0;
    }, [currentValue, progressPercent, loaded]);
    const fillStyle = useMemo(() => ({
        '--width': type === 'determinate' ? `${fillWidth}px` : '50%',
    }), [fillWidth]);
    const progressComplete = useMemo(() => fillWidth === progressTrackRef.current?.clientWidth, [fillWidth]);
    const fillClass = useMemo(() => classNames([
        styles.progress_fill,
        styles[type],
        styles[indeterminateStyle],
        progressComplete ? styles.complete : styles.progressive,
        RTL ? styles.rtl : '',
    ], {
        [styles[`progress_fill_${status}`]]: true,
    }), [progressComplete, status]);
    const wrapperStyle = useMemo(() => ({
        '--height': size === 'lg' ? `${40}px` : size === 'md' ? `${20}px` : `${10}px`,
        '--width': `${width}px`,
    }), []);
    const wrapperClass = useMemo(() => classNames(styles.progress_wrapper, {
        [styles[`progress_${status}`]]: true,
    }), [status]);
    const progressTrackClass = useMemo(() => classNames(styles.progress_track, {
        [styles[`progress_track_${status}`]]: true,
        [styles.progress_track_rtl]: RTL,
    }), [status]);
    const progressPercentValClass = useMemo(() => classNames(styles.progress_percent_value, {
        [styles[`progress_percent_value_${size}`]]: true,
    }), []);
    const onRef = useCallback((ref) => {
        progressTrackRef.current = ref;
        setLoaded(true);
    }, []);
    const ariaDefaultProps = useMemo(() => {
        const props = {};
        if (label) {
            props['aria-label'] = label;
        }
        if (statusText) {
            props['aria-valuetext'] = statusText;
        }
        return props;
    }, [statusText]);
    const ariaProps = useMemo(() => type === 'determinate' && {
        'aria-valuemax': maxValue,
        'aria-valuemin': 0,
        'aria-valuenow': Math.round(progressPercent * maxValue),
    }, [maxValue, progressPercent, statusText]);
    return (_jsx("div", { className: wrapperClass, role: "progressbar", style: wrapperStyle, ...ariaProps, ...ariaDefaultProps, children: _jsx("div", { className: progressTrackClass, ref: onRef, children: _jsx("span", { className: fillClass, style: fillStyle, children: canShowProgressValue && (_jsx("span", { className: progressPercentValClass, children: `${progressPercentValue}` })) }) }) }));
};
Progress.displayName = 'Progress';
export { Progress };
